import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ListChecks, AlertTriangle, Ban, Unlock, FolderKanban, ArrowUpRight, CalendarCheck, CalendarClock } from "lucide-react";
import { TaskItem } from "@/components/TaskItem";
import { BriefingCard } from "@/components/BriefingCard";
import { MyDayCard } from "@/components/myday/MyDayCard";
import { DashboardFilters } from "@/components/dashboard/DashboardFilters";
import { SavedViews } from "@/components/dashboard/SavedViews";
import { EMPTY_FILTERS } from "@/lib/dashboard";
import { BRAND_SKY } from "@/lib/brand";

const KPI_META = [
  { key: "my_tasks", label: "My Tasks", icon: ListChecks, accent: "text-slate-900", testid: "kpi-my-tasks-card" },
  { key: "overdue_actions", label: "Overdue Actions", icon: AlertTriangle, accent: "text-red-600", testid: "kpi-overdue-tasks-card" },
  { key: "due_today", label: "Due Today", icon: CalendarCheck, accent: "text-amber-600", testid: "kpi-due-today-card" },
  { key: "due_in_7_days", label: "Due in 7 Days", icon: CalendarClock, accent: "text-sky-600", testid: "kpi-due-7-days-card" },
  { key: "blocked_phases", label: "Blocked Phases", icon: Ban, accent: "text-red-600", testid: "kpi-blocked-projects-card" },
  { key: "erp_ready", label: "ERP Ready", icon: Unlock, accent: "text-emerald-600", testid: "kpi-erp-ready-card" },
  { key: "active_projects", label: "Active Projects", icon: FolderKanban, accent: "text-sky-600", testid: "kpi-active-projects-card" },
];

const LINKS = { my_tasks: "/tasks", overdue_actions: "/tasks", due_today: "/tasks", due_in_7_days: "/tasks", blocked_phases: "/projects", erp_ready: "/orders", active_projects: "/projects" };

export default function DashboardPage() {
  const { user } = useAuth();
  const canWide = ["admin", "project_coordinator"].includes(user?.role);

  const [summary, setSummary] = useState(null);
  const [filterKey, setFilterKey] = useState("my_tasks");
  const [scope, setScope] = useState(canWide ? "all" : "mine");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ customers: [], brands: [], managers: [] });
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [ready, setReady] = useState(false);

  const reloadViews = useCallback(() => api.get("/dashboard/views").then((r) => r.data.views || []), []);

  useEffect(() => {
    Promise.all([api.get("/projects/filters"), api.get("/dashboard/views")]).then(([f, v]) => {
      setOptions(f.data || {});
      const vs = v.data.views || [];
      setViews(vs);
      const def = vs.find((x) => x.is_default);
      if (def) {
        setScope(def.scope);
        setFilters({ ...EMPTY_FILTERS, ...def.filters });
        setActiveViewId(def.view_id);
      }
      setReady(true);
    }).catch(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return;
    api.get("/dashboard/summary", { params: { scope, ...filters } }).then((r) => setSummary(r.data)).catch(() => {});
  }, [ready, scope, filters]);

  const applyView = (id) => {
    if (!id) { setActiveViewId(null); return; }
    const v = views.find((x) => x.view_id === id);
    if (!v) return;
    setScope(v.scope);
    setFilters({ ...EMPTY_FILTERS, ...v.filters });
    setActiveViewId(id);
  };

  const onSave = async ({ name, is_default }) => {
    const r = await api.post("/dashboard/views", { name, scope, filters, is_default });
    setViews(await reloadViews());
    setActiveViewId(r.data.view_id);
    toast.success(`View "${name}" saved`);
  };
  const onUpdate = async (id) => {
    await api.put(`/dashboard/views/${id}`, { scope, filters });
    setViews(await reloadViews());
    toast.success("View updated");
  };
  const onRename = async (id, name) => {
    await api.put(`/dashboard/views/${id}`, { name });
    setViews(await reloadViews());
    toast.success("View renamed");
  };
  const onDelete = async (id) => {
    await api.delete(`/dashboard/views/${id}`);
    setViews(await reloadViews());
    if (activeViewId === id) setActiveViewId(null);
    toast.success("View deleted");
  };
  const onSetDefault = async (id, value) => {
    await api.put(`/dashboard/views/${id}`, { is_default: value });
    setViews(await reloadViews());
    toast.success(value ? "Set as default dashboard" : "Default removed");
  };

  const first = user?.name?.split(" ")[0] || "there";
  const feed = summary?.[filterKey] || [];
  const total = summary?.counts?.[filterKey] ?? 0;

  return (
    <div className="min-h-full">
      <section
        className="relative overflow-hidden text-white"
        style={{
          backgroundImage:
            `linear-gradient(to right, rgba(37,99,235,0.92), rgba(37,99,235,0.55) 55%, rgba(59,130,246,0.22)), url(${BRAND_SKY})`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
        }}
      >
        <div className="relative z-10 px-4 sm:px-8 lg:px-12 py-10 lg:py-14 fade-in-up">
          <p className="overline" style={{ color: "rgba(255,255,255,0.85)" }}>
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-3xl">
            Welcome back, {first}.
          </h1>
          <p className="mt-2 text-white/85 text-base max-w-xl">Everything that needs your attention, clearly in one place.</p>
          <div className="mt-6 flex flex-wrap gap-6 text-sm" data-testid="dashboard-hero-stats">
            <div><span className="text-white/60">Active:</span> <strong>{summary?.counts?.active_projects ?? "—"}</strong></div>
            <div><span className="text-white/60">Overdue:</span> <strong className="text-amber-300">{summary?.counts?.overdue_actions ?? "—"}</strong></div>
            <div><span className="text-white/60">Blocked:</span> <strong className="text-red-300">{summary?.counts?.blocked_phases ?? "—"}</strong></div>
            <div><span className="text-white/60">ERP ready:</span> <strong className="text-emerald-300">{summary?.counts?.erp_ready ?? "—"}</strong></div>
            <div><span className="text-white/60">Handed over:</span> <strong>{summary?.counts?.handed_over ?? "—"}</strong></div>
          </div>
        </div>
      </section>

      <div className="px-4 sm:px-8 lg:px-12 py-8 space-y-6">
        <BriefingCard />

        <div className="flex items-center gap-3 flex-wrap">
          <SavedViews views={views} activeViewId={activeViewId} onSelect={applyView} onSave={onSave}
            onUpdate={onUpdate} onRename={onRename} onDelete={onDelete} onSetDefault={onSetDefault} />
        </div>

        <DashboardFilters canWide={canWide} scope={scope} setScope={(v) => setScope(v)} filters={filters} setFilters={setFilters} options={options} />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-3 gap-3 content-start">
          {KPI_META.map((kpi) => {
            const Icon = kpi.icon;
            const count = summary?.counts?.[kpi.key] ?? 0;
            const active = filterKey === kpi.key;
            return (
              <button key={kpi.key} onClick={() => setFilterKey(kpi.key)} data-testid={kpi.testid}
                className={`text-left rounded-xl border p-4 bg-white transition hover:border-slate-900 hover:shadow-sm ${active ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`}>
                <div className="flex items-center justify-between"><Icon className={`h-4 w-4 ${kpi.accent}`} /><ArrowUpRight className="h-3.5 w-3.5 text-slate-300" /></div>
                <div className="mt-3 text-3xl font-bold tracking-tight text-slate-900 font-mono" data-testid={`${kpi.testid}-count`}>{count}</div>
                <div className="overline mt-1">{kpi.label}</div>
              </button>
            );
          })}
          </div>
          <MyDayCard />
        </div>

        <section>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="overline">Action feed</p>
              <h2 className="text-xl font-bold tracking-tight text-slate-900">{KPI_META.find((k) => k.key === filterKey)?.label} <span className="text-slate-400 font-normal text-base">({total})</span></h2>
            </div>
            <Link to={LINKS[filterKey]}>
              <Button variant="outline" className="h-9 border-slate-300" data-testid="dashboard-open-registry-btn">Open all <ArrowUpRight className="h-3.5 w-3.5 ml-1" /></Button>
            </Link>
          </div>
          {feed.length === 0 ? (
            <Card className="p-10 text-center text-slate-500 border-dashed border-slate-200 bg-white" data-testid="dashboard-feed-empty">Nothing here. You&apos;re all caught up.</Card>
          ) : (
            <div className="space-y-2" data-testid="dashboard-feed-list">
              {feed.map((i) => <TaskItem key={`${i.project_id}-${i.phase_id || ""}-${i.action_id || ""}`} item={i} testid={`dashboard-feed-row-${i.project_number}`} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
